import { useEffect, useRef } from 'react'

// Shared by every stacked overlay (popover, dropdown, panel) that wants
// Escape to dismiss it. A module-level LIFO stack of open instances so that
// when overlays are nested (e.g. a sutra Select open inside a
// FilterSelectionPopover pane), one Escape press closes only the topmost —
// without the stack, every open instance's listener would fire on the same
// keypress and close all of them at once. Registration order matches open
// order here because a nested overlay is always opened by a click after its
// parent is already mounted.
const openStack: symbol[] = []

interface UseEscapeDismissArgs {
  isOpen?: boolean
  onClose: () => void
}

/**
 * Closes the topmost open overlay on Escape, regardless of where DOM focus
 * currently is — a document-level, capture-phase listener rather than a
 * React `onKeyDown` bound to the overlay's own subtree. Capture phase is
 * required so this fires before a bubble-phase `stopPropagation()` an
 * overlay's own outside-click guard may call on itself (see sutra Select's
 * prior inline version of this same listener), and so
 * dashboards-v2-1's document-level tile-focus-dismiss listener (which also
 * calls `stopPropagation()` on match) never sees the keydown and deselects
 * the tile underneath.
 */
export const useEscapeDismiss = ({
  isOpen = true,
  onClose,
}: UseEscapeDismissArgs): void => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isOpen) return

    const id = Symbol('escape-dismiss')
    openStack.push(id)

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      if (openStack[openStack.length - 1] !== id) return

      event.preventDefault()
      event.stopPropagation()
      onCloseRef.current()
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      const index = openStack.indexOf(id)
      if (index !== -1) openStack.splice(index, 1)
    }
  }, [isOpen])
}
